import React, { useState, useEffect, useRef } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import { useAuth } from '../contexts/AuthContext';
import {
  subscribeToNotifications,
  unsubscribeFromNotifications,
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  archiveNotification
} from '../services/notificationService';

const NotificationCenter = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const dropdownRef = useRef(null);
  const channelRef = useRef(null);

  // Load notifications and setup real-time subscription
  useEffect(() => {
    if (user?.id) {
      loadNotifications();
      loadUnreadCount();
      setupRealtimeSubscription();
    }

    return () => {
      if (channelRef?.current) {
        unsubscribeFromNotifications(channelRef?.current);
      }
    };
  }, [user?.id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const setupRealtimeSubscription = () => {
    if (!user?.id) return;

    channelRef.current = subscribeToNotifications(user?.id, (newNotification) => {
      setNotifications((prev) => [newNotification, ...prev]);
      if (newNotification?.status === 'unread') {
        setUnreadCount((prev) => prev + 1);
        showBrowserNotification(newNotification);
      }
    });
  };

  const showBrowserNotification = (notification) => {
    if ('Notification' in window && Notification?.permission === 'granted') {
      new Notification(notification?.title, {
        body: notification?.message,
        icon: '/favicon.ico'
      });
    }
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification?.permission === 'default') {
      await Notification.requestPermission();
    }
  };

  const loadNotifications = async () => {
    if (!user?.id) return;

    setLoading(true);
    const { notifications: data } = await getNotifications(user?.id, {
      status: activeTab === 'unread' ? 'unread' : undefined,
      limit: 50
    });
    setNotifications(data);
    setLoading(false);
  };

  const loadUnreadCount = async () => {
    if (!user?.id) return;

    const count = await getUnreadCount(user?.id);
    setUnreadCount(count);
  };

  const handleMarkAsRead = async (notificationId) => {
    await markAsRead(notificationId);
    setNotifications((prev) =>
      prev?.map((n) =>
        n?.id === notificationId ? { ...n, status: 'read', readAt: new Date()?.toISOString() } : n
      )
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.id) return;

    const success = await markAllAsRead(user?.id);
    if (success) {
      setNotifications((prev) =>
        prev?.map((n) => ({ ...n, status: 'read', readAt: new Date()?.toISOString() }))
      );
      setUnreadCount(0);
    }
  };

  const handleArchive = async (notificationId) => {
    await archiveNotification(notificationId);
    setNotifications((prev) => prev?.filter((n) => n?.id !== notificationId));
    loadUnreadCount();
  };

  const handleNotificationClick = (notification) => {
    if (notification?.status === 'unread') {
      handleMarkAsRead(notification?.id);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'booking_confirmation':
        return 'CheckCircle';
      case 'payment_success':
        return 'CreditCard';
      case 'payment_failed':
        return 'AlertCircle';
      case 'trip_reminder':
        return 'Calendar';
      case 'payment_alert':
        return 'DollarSign';
      default:
        return 'Bell';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'booking_confirmation': case'payment_success':
        return 'text-green-600';
      case 'payment_failed':
        return 'text-red-600';
      case 'trip_reminder':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';

    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return time?.toLocaleDateString();
  };

  const filteredNotifications =
    activeTab === 'unread'
      ? notifications?.filter((n) => n?.status === 'unread')
      : notifications;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          requestNotificationPermission();
        }}
        className="relative p-2 text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted"
        aria-label="Notifications"
      >
        <Icon name="Bell" size={20} />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {/* Notification Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-card border border-border rounded-xl shadow-lg z-50 max-h-[600px] flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-foreground">Notifications</h3>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllAsRead}
                  className="text-xs"
                >
                  Mark all read
                </Button>
              )}
            </div>

            {/* Tabs */}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setActiveTab('all');
                  loadNotifications();
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  activeTab === 'all' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:bg-muted'
                }`}
              >
                All
              </button>
              <button
                onClick={() => {
                  setActiveTab('unread');
                  loadNotifications();
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  activeTab === 'unread' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:bg-muted'
                }`}
              >
                Unread {unreadCount > 0 && `(${unreadCount})`}
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">
                <Icon name="Loader" size={24} className="animate-spin mx-auto mb-2" />
                <p className="text-sm">Loading notifications...</p>
              </div>
            ) : filteredNotifications?.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Icon name="Bell" size={48} className="mx-auto mb-3 opacity-50" />
                <p className="text-sm font-medium">No notifications</p>
                <p className="text-xs mt-1">You're all caught up!</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filteredNotifications?.map((notification) => (
                  <div
                    key={notification?.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 hover:bg-muted transition-colors cursor-pointer ${
                      notification?.status === 'unread' ? 'bg-muted/50' : ''
                    }`}
                  >
                    <div className="flex gap-3">
                      <div
                        className={`flex-shrink-0 w-10 h-10 rounded-full bg-muted flex items-center justify-center ${
                          getNotificationColor(notification?.type)
                        }`}
                      >
                        <Icon name={getNotificationIcon(notification?.type)} size={20} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="text-sm font-semibold text-foreground truncate">
                            {notification?.title}
                          </h4>
                          {notification?.status === 'unread' && (
                            <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-1"></span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                          {notification?.message}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-muted-foreground">
                            {formatTimeAgo(notification?.createdAt)}
                          </span>
                          <button
                            onClick={(e) => {
                              e?.stopPropagation();
                              handleArchive(notification?.id);
                            }}
                            className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                          >
                            Archive
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;